import React, { useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { useTranslation } from "react-i18next";
import Common from "us.common";
import { Formik } from "formik";
import { IDetails } from "us.collection.admin/components/ManageFee/Interfaces";
import {
  INITIAL_DRAWER_DATA,
  DrawerType,
  ManageFeeAction,
  CALCULATION_TYPE_OPTIONS,
  CalculationType,
} from "us.collection.admin/components/ManageFee/Constants";
import { useLocation } from "react-router-dom";
import {
  getFeeDetailsById,
  getFeeBasisAmount,
  addFeeRangeToTable,
  checkRangeIsDirty,
  calculateReminderFee,
  calculateCollectionFee,
} from "us.collection.admin/components/ManageFee/Functions";
import { FeeRangeSWValidationSchema } from "us.collection.admin/components/ManageFee/Validations";
import { AddNewFeeRangeSW } from "us.collection.admin/components/ManageFee/Repository";
import * as Actions from "us.collection.admin/actions";
import "../../Home.scss";
import { $Select } from "us.common/components";
import { RootState } from "us.helper/types";
import { feeRangeInitialValues } from "./Constants";

const { $Button, $Form, $Popconfirm, $Input, $InputAmount, $Message } =
  Common.Components;

/**
 * @description - Fee Type Detail view
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3116171308/Manage+Fee+-+UI+Implementation+Design
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 26/08/2025
 */
const DetailsSW: React.FC<PropsFromRedux & IDetails> = (props) => {
  const { t } = useTranslation();
  const {
    actionType,
    currentCurrency,
    currentLanguage,
    feeRange,
    feeBasis,
    addNewFeeRange,
    changeDrawerDetails,
    savePayload,
    drawerInfo,
  } = props;

  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const typeId = urlParams.get("typeId");
  const feeId = urlParams.get("feeId");

  const [calculationType, setCalculationType] = React.useState(
    CalculationType.RATE
  );

  /**
   * @description - get selected Fee Range record from the Table
   */
  const feeData = useMemo(() => {
    setCalculationType(
      drawerInfo?.record?.calculationType ?? CalculationType.RATE
    );
    if (actionType === ManageFeeAction.EDIT) {
      return getFeeDetailsById(
        feeRange?.list,
        drawerInfo?.record?.id,
        actionType,
        DrawerType.FEE_RANGE
      );
    } else {
      return feeRangeInitialValues;
    }
  }, [feeRange?.list, drawerInfo?.record?.id, actionType]);

  /**
   * @description - Handle form submit
   * @param {any} values - Form values
   * @param {any} actions - Form Actions
   */
  const handleSubmit = (values: any, actions: any) => {
    actions.setSubmitting(true);
    const rangePayload = AddNewFeeRangeSW.call({ ...values, typeId, feeId });
    if (actionType === ManageFeeAction.ADD) {
      const payload = addFeeRangeToTable(rangePayload, feeRange?.list);
      if (payload == -1) {
        $Message.error(
          t(`US.COLLECTION.ADMIN:MANAGE_FEE.FEE_RANGE_ALREADY_EXISTS`)
        );
      } else {
        addNewFeeRange && addNewFeeRange(payload);
      }
    } else if (actionType === ManageFeeAction.EDIT) {
      addNewFeeRange &&
        addNewFeeRange({ addActionPayload: rangePayload, feeId });
    }
  };
  /**
   * @description - Calculate Reminder Fee and Collection Fee by Fee Rate
   * @param val - Fee Rate value
   * @param setFieldValue - Formik setFieldValue function
   * @param values - formik values
   */
  const handleReminderFeeRateChange = (
    rate: number,
    setFieldValue: any,
    values: any
  ) => {
    try {
      if (calculationType === CalculationType.RATE) {
        setFieldValue("feeRate", rate);
        const reminderFee =
          ((Number(values?.baseAmount) ?? 0) * (Number(rate) ?? 0)) / 100;
        setFieldValue("reminderFee", Number(reminderFee.toFixed(2)));
        const collectionFee =
          Number(values?.baseAmount.toFixed(2)) -
          Number(reminderFee.toFixed(2));
        setFieldValue("collectionFee", Number(collectionFee.toFixed(2)));
      }
    } catch (err) {
      setFieldValue("feeRate", rate);
      setFieldValue("reminderFee", 0);
      setFieldValue("collectionFee", 0);
    }
  };

  /**
   * @description - Calculate Reminder Fee and Collection Fee by Base Amount
   * @param amount - Base Amount value
   * @param setFieldValue - Formik setFieldValue function
   * @param values - formik values
   */
  const handleBaseAmountChange = (
    amount: number,
    setFieldValue: any,
    values: any
  ) => {
    try {
      if (calculationType === CalculationType.RATE) {
        setFieldValue("baseAmount", amount);
        const reminderFee = calculateReminderFee(amount, values?.feeRate);
        setFieldValue("reminderFee", Number(reminderFee.toFixed(2)));
        const collectionFee = calculateCollectionFee(amount, reminderFee);
        setFieldValue("collectionFee", Number(collectionFee.toFixed(2)));
      } else {
        setFieldValue("baseAmount", amount);
      }
    } catch (err) {
      setFieldValue("baseAmount", amount);
      setFieldValue("reminderFee", 0);
      setFieldValue("collectionFee", 0);
    }
  };

  /**
   * @description - Handle Calculation Type change
   * @param val - Calculation Type value
   * @param setFieldValue - Formik setFieldValue function
   */
  const handleCalculationTypeChange = (
    val: CalculationType,
    setFieldValue: any
  ) => {
    if (val === CalculationType.FIXED) {
      setFieldValue("feeRate", 0);
      setFieldValue("baseAmount", 0);
    } else {
      setFieldValue("feeRate", 0);
      setFieldValue("baseAmount", 0);
      setFieldValue("reminderFee", 0);
      setFieldValue("collectionFee", 0);
    }
    setCalculationType(val);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...feeData,
        feeBasis: getFeeBasisAmount(feeBasis?.list, Number(feeId), actionType),
        rangeLength: feeRange?.list?.length,
      }}
      validationSchema={FeeRangeSWValidationSchema}
      validateOnBlur
      validateOnChange
      onSubmit={handleSubmit}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        ...restProps
      }: any) => (
        <>
          <$Form layout="vertical" autoComplete="off" onSubmit={handleSubmit}>
            <div>
              <div className="mb-3">
                <$InputAmount
                  name="fromAmount"
                  value={values?.fromAmount}
                  label={t("US.COLLECTION.ADMIN:MANAGE_FEE.FROM_AMOUNT")}
                  currentLanguage={currentLanguage}
                  currentCurrency={currentCurrency}
                  size="small"
                  className="w-100"
                  disabled={actionType === ManageFeeAction.EDIT}
                  max={999999999999999}
                  min={0}
                />
              </div>
              <div className="mb-3">
                <$InputAmount
                  name="toAmount"
                  value={values?.toAmount}
                  label={t("US.COLLECTION.ADMIN:MANAGE_FEE.TO_AMOUNT")}
                  currentLanguage={currentLanguage}
                  currentCurrency={currentCurrency}
                  size="small"
                  className="w-100"
                  disabled={actionType === ManageFeeAction.EDIT}
                  max={999999999999999}
                  required={values.rangeLength != 0 ? true : undefined}
                  min={0}
                />
              </div>
              <div className="mb-3">
                <$Select
                  name="calculationType"
                  formitem={{
                    label: t("US.COLLECTION.ADMIN:MANAGE_FEE.CALCULATION_TYPE"),
                  }}
                  allOption={false}
                  required
                  defaultValue={CalculationType.RATE}
                  options={CALCULATION_TYPE_OPTIONS}
                  data-testid="calculationType"
                  onChange={(val: any) => {
                    handleCalculationTypeChange(val, restProps.setFieldValue);
                  }}
                />
              </div>
              {calculationType === CalculationType.RATE && (
                <>
                  <div className="mb-3">
                    <$InputAmount
                      name="baseAmount"
                      value={values?.baseAmount}
                      label={t("US.COLLECTION.ADMIN:MANAGE_FEE.BASE_AMOUNT")}
                      currentLanguage={currentLanguage}
                      currentCurrency={currentCurrency}
                      size="small"
                      className="w-100"
                      // disabled
                      required
                      onChange={(e: any) => {
                        handleBaseAmountChange(
                          e,
                          restProps.setFieldValue,
                          values
                        );
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <$InputAmount
                      name="feeRate"
                      value={values?.feeRate}
                      label={t(
                        "US.COLLECTION.ADMIN:MANAGE_FEE.REMINDER_FEE_RATE"
                      )}
                      currentLanguage={currentLanguage}
                      currentCurrency={currentCurrency}
                      size="small"
                      className="w-100"
                      required
                      addonAfter="%"
                      onChange={(e: any) => {
                        handleReminderFeeRateChange(
                          e,
                          restProps.setFieldValue,
                          values
                        );
                      }}
                      disabled={!(values?.baseAmount > 0)}
                      max={100}
                      min={0}
                    />
                  </div>
                </>
              )}
              <div className="mb-3">
                <$InputAmount
                  name="reminderFee"
                  value={values?.reminderFee}
                  label={t(
                    "US.COLLECTION.ADMIN:MANAGE_FEE.REMINDER_FEE_AMOUNT"
                  )}
                  currentLanguage={currentLanguage}
                  currentCurrency={currentCurrency}
                  size="small"
                  className="w-100"
                  disabled={calculationType === CalculationType.RATE}
                  required
                />
              </div>
              <div className="mb-3">
                <$InputAmount
                  name="collectionFee"
                  value={values?.collectionFee}
                  label={t(
                    "US.COLLECTION.ADMIN:MANAGE_FEE.COLLECTION_FEE_AMOUNT"
                  )}
                  currentLanguage={currentLanguage}
                  currentCurrency={currentCurrency}
                  size="small"
                  className="w-100"
                  disabled={calculationType === CalculationType.RATE}
                />
              </div>
            </div>
            <div className="drawer-footer-fixed align-content-center justify-content-end">
              <div>
                <$Button
                  type="primary"
                  onClick={handleSubmit}
                  className="mr-2"
                  data-testid="save-btn"
                  loading={savePayload?.isSaving}
                  disabled={
                    !checkRangeIsDirty(actionType, values, feeData) ||
                    !restProps.isValid
                  }
                >
                  {actionType === ManageFeeAction.ADD
                    ? t("US.COMMON:COMMON.SAVE")
                    : t("US.COMMON:COMMON.UPDATE")}
                </$Button>
                {checkRangeIsDirty(actionType, values, feeData) && (
                  <$Popconfirm
                    title={t(
                      "US.COMMON:COMMON.ARE_YOU_SURE_YOU_WANT_TO_DISCARD_THE_CHANGES_?"
                    )}
                    placement="topLeft"
                    onConfirm={() =>
                      changeDrawerDetails &&
                      changeDrawerDetails(INITIAL_DRAWER_DATA)
                    }
                    okText={t("US.COMMON:COMMON.YES")}
                    cancelText={t("US.COMMON:COMMON.NO")}
                  >
                    <$Button>{t("US.COMMON:COMMON.CANCEL")}</$Button>
                  </$Popconfirm>
                )}
                {!checkRangeIsDirty(actionType, values, feeData) && (
                  <$Button
                    onClick={() =>
                      changeDrawerDetails &&
                      changeDrawerDetails(INITIAL_DRAWER_DATA)
                    }
                  >
                    {t("US.COMMON:COMMON.CANCEL")}
                  </$Button>
                )}
              </div>
            </div>
          </$Form>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { common, manageFee } = state;
  const { currentDateFormat, currentCurrency, currentLanguage } = common;
  const { feeBasis, feeRange, savePayload, drawerInfo } = manageFee;
  return {
    currentDateFormat,
    currentCurrency,
    currentLanguage,
    feeBasis,
    feeRange,
    savePayload,
    drawerInfo,
  };
};
const { newFeeRange, feeTypeActions, feeRangeActions } = Actions.manageFee;
const { openDrawer } = feeTypeActions;

const mapDispatchToProps = {
  addNewFeeRange: newFeeRange.save,
  changeDrawerDetails: openDrawer,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(DetailsSW);
